/** Processos em andamento e encerrados; iniciar um novo a partir de um fluxo. */
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ClientesPagina, FluxoResumo, ProcessoDetalhe, StatusProcesso } from '@sgc/contracts';
import { get, post } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmtDataHora, useInvalidarTarefas } from '../lib/tarefas';
import { Campo, Sel } from '../ui/campos';
import { SelCliente } from '../ui/sel-cliente';
import { SelFuncionario } from '../ui/sel-funcionario';
import { Kpi, Modal, Pill, Sec, Tabs } from '../ui/layout';
import { useToast } from '../ui/toast';
import { CampoDinamico, dadosParaApi } from '../telas/agenda/detalhe/processo';

export const Route = createFileRoute('/_app/processos/')({ component: Processos });

export const ROTULO_STATUS_PROC: Record<StatusProcesso, string> = { ANDAMENTO: 'Em andamento', CONCLUIDO: 'Concluído', REPROVADO: 'Reprovado', CANCELADO: 'Cancelado' };
export const COR_STATUS_PROC: Record<StatusProcesso, 'azul' | 'verde' | 'verm' | undefined> = { ANDAMENTO: 'azul', CONCLUIDO: 'verde', REPROVADO: 'verm', CANCELADO: undefined };

type Pagina = { itens: Array<{ id: string; fluxoNome: string; clienteNome: string | null; titulo: string; status: StatusProcesso; etapaAtualOrdem: number | null; etapaAtualNome: string | null; totalEtapas: number; responsavelAtualApelido: string | null; tarefaAtualId: string | null; abertoPorApelido: string | null; abertoEm: string }>; total: number; resumo: { andamento: number; concluidos: number; reprovados: number } };

function Processos() {
  const { eu } = useAuth();
  const papel = eu?.escritorio?.papel;
  const podeEditar = papel === 'ADMIN' || papel === 'SUPERVISOR' || papel === 'OPERADOR';
  const [status, setStatus] = useState<StatusProcesso | ''>('ANDAMENTO');
  const [busca, setBusca] = useState('');
  const [novo, setNovo] = useState(false);
  const q = useQuery({ queryKey: ['processos', status, busca], queryFn: () => get<Pagina>('/v1/processos', { status: status || undefined, busca: busca || undefined, perPage: 100 }), placeholderData: (p) => p });
  const r = q.data;
  return (
    <>
      <div className="topbar"><div><h1>Processos</h1><div className="sub">instâncias dos fluxos — cada etapa é uma tarefa na agenda</div></div>
        <div className="acoes">{podeEditar && <button className="btn primario" type="button" onClick={() => setNovo(true)}>+ Iniciar processo</button>}</div></div>
      <div className="conteudo">
        {r && <div className="kpis"><Kpi rotulo="Em andamento" valor={r.resumo.andamento} cor="azul" /><Kpi rotulo="Concluídos" valor={r.resumo.concluidos} cor="verde" /><Kpi rotulo="Reprovados" valor={r.resumo.reprovados} cor={r.resumo.reprovados ? 'verm' : undefined} /></div>}
        <Sec titulo="Filtros">
          <div className="fgrid">
            <div className="c6 field"><span className="rot">Situação</span><Tabs valor={status} onChange={setStatus} itens={[{ id: 'ANDAMENTO', rotulo: 'Em andamento' }, { id: 'CONCLUIDO', rotulo: 'Concluídos' }, { id: 'REPROVADO', rotulo: 'Reprovados' }, { id: '', rotulo: 'Todos' }]} /></div>
            <Campo className="c4" rotulo="Título" valor={busca} onChange={setBusca} placeholder="buscar…" />
          </div>
        </Sec>
        <div className="tbwrap">
          <table className="tb">
            <thead><tr><th>Processo</th><th>Fluxo</th><th>Cliente</th><th>Etapa atual</th><th>Resp.</th><th>Situação</th><th>Aberto</th></tr></thead>
            <tbody>
              {r?.itens.length === 0 && <tr><td className="vazio" colSpan={7}>nenhum processo{status === 'ANDAMENTO' ? ' em andamento' : ''}</td></tr>}
              {r?.itens.map((p) => (
                <tr key={p.id}>
                  <td><Link to="/processos/$id" params={{ id: p.id }}><b>{p.titulo}</b></Link></td><td>{p.fluxoNome}</td><td>{p.clienteNome ?? <span className="dim">—</span>}</td>
                  <td>{p.etapaAtualOrdem ? <>{p.tarefaAtualId ? <Link to="/tarefas" search={{ aba: 'lista', t: p.tarefaAtualId }}>{p.etapaAtualOrdem}. {p.etapaAtualNome}</Link> : `${p.etapaAtualOrdem}. ${p.etapaAtualNome}`} <span className="dim">de {p.totalEtapas}</span></> : <span className="dim">—</span>}</td>
                  <td className="mono">{p.responsavelAtualApelido ?? '—'}</td><td><Pill cor={COR_STATUS_PROC[p.status]}>{ROTULO_STATUS_PROC[p.status]}</Pill></td><td className="mono dim">{fmtDataHora(p.abertoEm)} · {p.abertoPorApelido}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {novo && <ModalIniciar onFechar={() => setNovo(false)} />}
    </>
  );
}

function ModalIniciar({ onFechar }: { onFechar: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const invalidar = useInvalidarTarefas();
  const fluxos = useQuery({ queryKey: ['fluxos'], queryFn: () => get<FluxoResumo[]>('/v1/fluxos') });
  const [fluxoId, setFluxoId] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [funcionarioId, setFuncionarioId] = useState('');
  const [titulo, setTitulo] = useState('');
  const [dados, setDados] = useState<Record<string, unknown>>({});
  const f = fluxos.data?.find((x) => x.id === fluxoId);
  const iniciar = useMutation({
    mutationFn: () => post<ProcessoDetalhe>('/v1/processos', { fluxoId, clienteId: clienteId || null, funcionarioId: funcionarioId || null, titulo: titulo || null, dados: dadosParaApi(f?.camposAbertura ?? [], dados) }),
    onSuccess: (p) => { void qc.invalidateQueries({ queryKey: ['processos'] }); invalidar(); toast.ok(`processo iniciado — etapa 1: ${p.etapaAtualNome}`); onFechar(); },
    onError: (e) => toast.erro(e),
  });
  const pronto = !!f && (!f.clienteObrigatorio || !!clienteId) && f.camposAbertura.every((c) => !c.obrigatorio || (dados[c.chave] !== undefined && dados[c.chave] !== '' && dados[c.chave] !== null));
  return (
    <Modal titulo="Iniciar processo" aberto onFechar={onFechar}
      acoes={<><button className="btn" type="button" onClick={onFechar}>Cancelar</button><button className="btn primario" type="button" disabled={!pronto || iniciar.isPending} onClick={() => iniciar.mutate()}>{iniciar.isPending ? 'iniciando…' : 'Iniciar'}</button></>}>
      <div className="fgrid">
        <Sel className="c6" rotulo="Fluxo" obrigatorio valor={fluxoId} vazio="escolha…" opcoes={(fluxos.data ?? []).filter((x) => x.ativo).map((x) => [x.id, `${x.nome} (${x.etapas} etapas)`] as const)} onChange={(v) => { setFluxoId(v); setDados({}); }} />
        <SelCliente className="c6" obrigatorio={!!f?.clienteObrigatorio} valor={clienteId} vazio="— sem cliente —" onChange={(v) => { setClienteId(v); setFuncionarioId(''); }} />
        <SelFuncionario className="c12" clienteId={clienteId} valor={funcionarioId} ajuda="o processo é sobre uma pessoa? o nome entra no título e em cada etapa"
          onChange={(v, func) => { setFuncionarioId(v); if (func && f?.camposAbertura.some((c) => c.chave === 'funcionario')) setDados((d) => ({ ...d, funcionario: func.nome })); }} />
        <Campo className="c12" rotulo="Título" valor={titulo} onChange={setTitulo} placeholder={f ? `${f.nome}${clienteId ? ' — cliente' : ''}` : 'vazio = nome do fluxo + cliente'} />
        {f?.camposAbertura.map((c) => <CampoDinamico key={c.chave} c={c} valor={dados[c.chave]} onChange={(v) => setDados({ ...dados, [c.chave]: v })} />)}
        {f?.descricao && <div className="c12 fnote">{f.descricao}</div>}
      </div>
    </Modal>
  );
}
